import React, {useCallback, useEffect, useMemo, useState} from "react";
import Form from "../../base/form";
import {Col, Row} from "reactstrap";
import Button from "../../base/button/button";
import classnames from "classnames";
import {InputForm} from "../../base/input/input";
import {DatePickerForm} from "../../base/date-picker";
import {MuiRFF, Yup} from '../../form'
import DateUtils from "../../../../core/services/date-utils";
import MultiCustomerSearchSectionAutocomplete from "../../text-fields/autocomplete/customer-autocomplete/multi-search-section";

const schema = Yup.object().shape({
    startDate: Yup.mixed().nullable(),
    endDate: Yup.mixed().nullable(),
    keyword: Yup.string().trim().nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const ShippingFeesSearchSection = ({searchData, hideCustomersMultiSelect}) => {
    const {setCustomers: searchDataSetCustomers} = searchData;
    const [customers, _setCustomers] = useState(searchData.customers)

    /**
     * Sets the initial values of the customers in the state.
     */
    useEffect(() => {
        _setCustomers(searchData.customers);
    }, [searchData.customers]);

    /**
     * Sets the customers in the state and the search data.
     * @type {function(*=): void}
     */
    const setCustomers = useCallback((stateOrCallback) => {
        _setCustomers(prevState => {
            let newState;
            if (typeof stateOrCallback === 'function') {
                newState = stateOrCallback(prevState);
            } else {
                newState = stateOrCallback;
            }
            searchDataSetCustomers(newState);
            return newState;
        })
    }, [searchDataSetCustomers]);

    const initialValues = useMemo(() => ({
        startDate: searchData.filters?.startDate ? DateUtils.serverToUTC(searchData.filters?.startDate) : undefined,
        endDate: searchData.filters?.endDate ? DateUtils.serverToUTC(searchData.filters?.endDate) : undefined,
        keyword: searchData.filters?.keyword?.toString(),
    }), [searchData.filters])

    /**
     * Changes the query of the url to the query containing the searched values so that the list pages of orders can
     * use them and search related items.
     * @param values {any} form values
     */
    const onSubmit = useCallback((values) => {
        searchData.setFilters({
            ...values,
            startDate: values.startDate ? DateUtils.UTCToServer(values.startDate).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values.endDate ? DateUtils.UTCToServer(values.endDate).format(DateUtils.withoutOffsetFormat) : undefined,
        });
        searchDataSetCustomers(customers);
    }, [searchData, customers, searchDataSetCustomers])

    const utcNow = useMemo(() => DateUtils.UTCNow(), []);

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({submitting, values, form}) => {
                const colClassName = 'px-3 mb-4';
                return (
                    <Row className='align-items-center justify-content-center'>
                        {
                            !hideCustomersMultiSelect &&
                            <Col xs={12} className={colClassName}>
                                <MultiCustomerSearchSectionAutocomplete
                                    customers={customers}
                                    setCustomers={setCustomers}
                                />
                            </Col>
                        }
                        <Col xs={12} className={classnames(colClassName)}>
                            <InputForm
                                onChange={(val) => form.change('keyword', val)}
                                value={values?.keyword ?? ''}
                                label={'Keywords'}
                                name={'keyword'}
                                type={'text'}
                                required={required.keyword}
                            />
                        </Col>
                        <Col xs={6} className={colClassName}>
                            <DatePickerForm
                                name={'startDate'}
                                label={'Start Date'}
                                required={required.startDate}
                                maxDate={values.endDate ?? utcNow}
                            />
                        </Col>
                        <Col xs={6} className={colClassName}>
                            <DatePickerForm
                                name={'endDate'}
                                label={'End Date'}
                                required={required.endDate}
                                minDate={values.startDate}
                                maxDate={utcNow}
                            />
                        </Col>
                        <Col xs={12}
                             className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'>
                            <Button
                                disabled={!!submitting}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className='py-3 px-8'
                            >
                                Search
                            </Button>
                        </Col>
                    </Row>
                );
            }}
        />
    );
}

export default ShippingFeesSearchSection;
